/*
  Contact Form Component
  ----------------------
  Lead capture form with free email validation.
  Requires company website for free email providers unless bypassed.
*/

import React from 'react';
import { isFreeEmail } from '../../utils/formatters';

/**
 * Contact form for lead capture
 * @param {Object} props
 * @param {Object} props.contactInfo - Contact info state { name, email, phone, company, website, noWebsite }
 * @param {Function} props.onChange - Handler for input changes
 * @param {Function} props.onSubmit - Form submission handler
 * @param {Function} props.onContactInfoUpdate - Direct update handler for contactInfo fields
 * @param {Function} props.t - Translation function
 */
const ContactForm = ({ contactInfo, onChange, onSubmit, onContactInfoUpdate, t }) => {
  const showWebsiteField = isFreeEmail(contactInfo.email);

  const handleCheckboxChange = (e) => {
    // Use direct update if available, otherwise fall back to onChange
    if (onContactInfoUpdate) {
      onContactInfoUpdate({
        noWebsite: e.target.checked,
        website: e.target.checked ? '' : contactInfo.website
      });
    } else {
      onChange({
        target: {
          name: 'contactInfo.noWebsite',
          value: e.target.checked,
          type: 'checkbox'
        }
      });
      if (e.target.checked) {
        onChange({
          target: {
            name: 'contactInfo.website',
            value: ''
          }
        });
      }
    }
  };

  return (
    <div className="mt-8 p-6 bg-gray-50 rounded-lg border border-gray-200">
      <h3 className="text-lg font-semibold mb-4">{t('getYourDetailedReport')}</h3>
      <form onSubmit={onSubmit} className="space-y-4">
        {/* Full Name */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700">
            {t('fullName')}
          </label>
          <input
            type="text"
            id="name"
            name="contactInfo.name"
            value={contactInfo.name}
            onChange={onChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none sm:text-sm"
            required
          />
        </div>
        
        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            {t('emailAddress')}
          </label>
          <input
            type="email"
            id="email"
            name="contactInfo.email"
            value={contactInfo.email}
            onChange={onChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none sm:text-sm"
            required
          />
          {showWebsiteField && (
            <p className="mt-1 text-xs text-amber-600">
              {t('freeEmailNotice', 'Using a personal email? Please provide your company website below to receive the full report.')}
            </p>
          )}
        </div>
        
        {/* Company Name */}
        <div>
          <label htmlFor="company" className="block text-sm font-medium text-gray-700">
            {t('companyName')}
          </label>
          <input
            type="text"
            id="company"
            name="contactInfo.company"
            value={contactInfo.company}
            onChange={onChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none sm:text-sm"
            required
          />
        </div>

        {/* Website field - required for free email addresses unless noWebsite is checked */}
        {showWebsiteField && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
            <div>
              <label htmlFor="website" className="block text-sm font-medium text-gray-700">
                {t('companyWebsite', 'Company Website')} {!contactInfo.noWebsite && <span className="text-red-500">*</span>}
              </label>
              <input
                type="url"
                id="website"
                name="contactInfo.website"
                value={contactInfo.website}
                onChange={onChange}
                placeholder="https://www.yourcompany.com"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none sm:text-sm"
                required={!contactInfo.noWebsite}
                disabled={contactInfo.noWebsite}
              />
              <p className="mt-1 text-xs text-gray-500">
                {t('websiteHelp', 'Required when using a personal email address to verify your business.')}
              </p>
            </div>
            
            <div className="mt-3 flex items-start">
              <input
                type="checkbox"
                id="noWebsite"
                name="contactInfo.noWebsite"
                checked={contactInfo.noWebsite}
                onChange={handleCheckboxChange}
                className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="noWebsite" className="ml-2 text-sm text-gray-600">
                {t('noWebsiteCheckbox', 'My business does not have a website')}
              </label>
            </div>
          </div>
        )}
        
        {/* Submit Button */}
        <div className="pt-2">
          <button
            type="submit"
            className="w-full btn-primary"
          >
            {t('getMyDetailedReport')}
          </button>
        </div>
        
        <p className="text-xs text-gray-500 mt-2">
          {t('detailedReportNote')}
        </p>
      </form>
    </div>
  );
};

export default ContactForm;
